import Box from '@mui/material/Box';
import { API } from 'aws-amplify';
import React from 'react';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { AgentSelect } from '../types';
import { captureError } from '../utils/capture-error';
import {
  ImpersonationSearch,
  SearchAgent,
} from './alliance/impersonation-search';

/** "First Last" for an ARC /agent/search row, falling back to agentname */
const agentName = (params: { agent: AgentSelect }) =>
  [params.agent.fname, params.agent.lname].filter(Boolean).join(' ') ||
  params.agent.agentname ||
  params.agent.agentno;

/** ARC's lowercase /agent/search row as a SearchAgent */
const toSearchAgent = (params: { agent: AgentSelect }): SearchAgent => {
  // Params
  const { agent } = params;
  return {
    id: agent.agentno,
    name: agentName({ agent }),
    city: agent.home_city,
    state: agent.home_state,
    status: agent.status,
    raw: agent,
  };
};

/**
 * The impersonation search in the page header (about 24 pages), wired to
 * ARC: searches the Dashboard API's /agent/search (which scopes results to
 * what the user may see) and keeps the picked agent in AppContext, in memory
 * only, as before. Pages read `impersonatedAgent.agentno` from AppContext.
 *
 * 320px at md+, full width on its own row below md.
 *
 * @param disabled - Off while the page is loading
 * @param darkMode - Dark header (most pages) or the light variant
 */
export function SelectAgentImpersonate(props: {
  disabled?: boolean;
  darkMode?: boolean;
}) {
  // Context
  const { state } = React.useContext(AppContext);
  const {
    state: { user },
  } = React.useContext(AuthContext);
  // Hooks
  const identity = useAllianceIdentity();
  const { startImpersonating, stopImpersonating } = useSessionActions();

  const username: string = user?.getUsername() || '';

  const handleSearch = React.useCallback(
    async (params: { query: string }) => {
      const body = { search: params.query };
      const path = '/agent/search';
      try {
        const response: { data: AgentSelect[] } = await API.post(
          'DashboardAPI',
          path,
          { body },
        );
        return (response.data || [])
          .filter((agent) => agent.agentno && agent.agentno !== username)
          .map((agent) => toSearchAgent({ agent }));
      } catch (error) {
        // A 403 means the agent is outside what this user may search (e.g.
        // not in their downline); the search shows it as no results
        if (
          (error as { response?: { status?: number } })?.response?.status !==
          403
        ) {
          captureError({ data: { error, request: { body, path } } });
        }
        throw error;
      }
    },
    [username],
  );

  if (!identity) {
    return null;
  }

  const impersonated = state.impersonatedAgent;
  const active = impersonated
    ? { id: impersonated.agentno, name: agentName({ agent: impersonated }) }
    : null;

  return (
    <Box sx={{ width: { xs: '100%', md: 320 }, flex: 'none' }}>
      <ImpersonationSearch
        placement="header"
        mode={props.darkMode ? 'dark' : 'light'}
        scope={identity.role}
        staffAgentName={identity.staffFor?.fullName}
        owner={username}
        active={active}
        disabled={props.disabled}
        onSearch={handleSearch}
        onPick={(agent) => startImpersonating(agent.raw as AgentSelect)}
        onStop={stopImpersonating}
        excludeIds={[identity.user.id, identity.staffFor?.id || ''].filter(
          Boolean,
        )}
      />
    </Box>
  );
}
